import React, { useState, useEffect } from "react";
import {
  Paper,
  Typography,
  Button,
  Box,
  CircularProgress,
  Alert,
  Chip,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
} from "@mui/material";
import { Download as DownloadIcon } from "@mui/icons-material";
import { InvoiceFilters, InvoiceSummary } from "../types/invoice";
import { ApiService } from "../services/api";
import StatsSummary from "./StatsSummary";

const ExportSection: React.FC = () => {
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;

  const [month, setMonth] = useState<number>(currentMonth);
  const [year, setYear] = useState<number>(currentYear);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const [exportSuccess, setExportSuccess] = useState(false);
  const [stats, setStats] = useState<InvoiceSummary[]>([]);
  const [statsLoading, setStatsLoading] = useState(false);

  const months = [
    { value: 1, label: "Janeiro" },
    { value: 2, label: "Fevereiro" },
    { value: 3, label: "Março" },
    { value: 4, label: "Abril" },
    { value: 5, label: "Maio" },
    { value: 6, label: "Junho" },
    { value: 7, label: "Julho" },
    { value: 8, label: "Agosto" },
    { value: 9, label: "Setembro" },
    { value: 10, label: "Outubro" },
    { value: 11, label: "Novembro" },
    { value: 12, label: "Dezembro" },
  ];

  const years = Array.from({ length: 6 }, (_, i) => currentYear - i);

  const handleExportCSV = async () => {
    setExporting(true);
    setExportError(null);
    setExportSuccess(false);

    try {
      // Use date range filters to export by emission date (not reporting month)
      // This shows when invoices were actually issued, not when they're reported
      const firstDay = new Date(Date.UTC(year, month - 1, 1));
      const lastDay = new Date(Date.UTC(year, month, 0));

      const data_inicio = firstDay.toISOString().split("T")[0];
      const data_fim = lastDay.toISOString().split("T")[0];

      const exportFilters: InvoiceFilters = {
        data_inicio,
        data_fim,
      };

      const blob = await ApiService.exportInvoicesCSV(exportFilters);

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;

      // Generate filename with year and month
      const monthName = months.find((m) => m.value === month)?.label || month;
      const filename = `faturas_${monthName}_${year}`;

      link.download = `${filename}.csv`;
      document.body.appendChild(link);
      link.click();

      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setExportSuccess(true);
      setTimeout(() => setExportSuccess(false), 3000);
    } catch (error) {
      console.error("Export error:", error);
      setExportError(
        error instanceof Error
          ? error.message
          : "Erro ao exportar faturas. Tente novamente."
      );
    } finally {
      setExporting(false);
    }
  };

  const getActiveFiltersDescription = () => {
    const descriptions: string[] = [];

    // Only show the selected period (no other filters)
    const monthName = months.find((m) => m.value === month)?.label || month;
    descriptions.push(`Período: ${monthName} de ${year}`);

    return descriptions;
  };

  const activeFilters = getActiveFiltersDescription();

  // Load stats for the selected period
  useEffect(() => {
    const loadStats = async () => {
      setStatsLoading(true);
      try {
        // Use mes/ano filters for stats to respect mes_relatorio (accounting period)
        // This shows expenses in their reporting month, not emission date
        const periodFilters: InvoiceFilters = {
          mes: month.toString(),
          ano: year,
        };

        const response = await ApiService.getInvoiceStats(periodFilters);
        const summaryData = response.data || [];
        setStats(summaryData);
      } catch (error) {
        console.error("Error loading stats:", error);
        setStats([]);
      } finally {
        setStatsLoading(false);
      }
    };

    loadStats();
  }, [month, year]);

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        Exportar Relatório
      </Typography>

      <Typography variant="body2" color="text.secondary" paragraph>
        Selecione o período e exporte um arquivo CSV com as faturas.
      </Typography>

      {/* Statistics Summary for selected period */}
      {statsLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={{ mb: 3 }}>
          <StatsSummary stats={stats} loading={false} />
        </Box>
      )}

      {/* Period Selection */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle2" gutterBottom>
          Selecionar Período:
        </Typography>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControl fullWidth>
              <InputLabel id="export-month-label">Mês</InputLabel>
              <Select
                labelId="export-month-label"
                id="export-month"
                value={month}
                label="Mês"
                onChange={(e) => setMonth(e.target.value as number)}
              >
                {months.map((m) => (
                  <MenuItem key={m.value} value={m.value}>
                    {m.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControl fullWidth>
              <InputLabel id="export-year-label">Ano</InputLabel>
              <Select
                labelId="export-year-label"
                id="export-year"
                value={year}
                label="Ano"
                onChange={(e) => setYear(e.target.value as number)}
              >
                {years.map((y) => (
                  <MenuItem key={y} value={y}>
                    {y}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Box>

      {/* Active Filters Display */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle2" gutterBottom>
          Filtros a Aplicar:
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
          {activeFilters.map((filter, index) => (
            <Chip
              key={index}
              label={filter}
              size="small"
              variant="outlined"
              color="primary"
            />
          ))}
        </Stack>
      </Box>

      {/* Export Messages */}
      {exportError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {exportError}
        </Alert>
      )}

      {exportSuccess && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Relatório exportado com sucesso!
        </Alert>
      )}

      {/* Export Button */}
      <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
        <Button
          variant="contained"
          startIcon={
            exporting ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              <DownloadIcon />
            )
          }
          onClick={handleExportCSV}
          disabled={exporting}
          size="large"
        >
          {exporting ? "Exportando..." : "Exportar CSV"}
        </Button>

        <Box>
          <Typography variant="caption" color="text.secondary">
            Formato: CSV (compatível com Excel)
          </Typography>
        </Box>
      </Box>

      {/* Export Info */}
      <Box sx={{ mt: 3, p: 2, bgcolor: "grey.50", borderRadius: 1 }}>
        <Typography variant="subtitle2" gutterBottom>
          ℹ️ Sobre o arquivo CSV:
        </Typography>
        <Typography variant="body2" color="text.secondary">
          • Contém todas as colunas: ID, Número, NIFs, Valor, Datas, Categoria e
          Descrição
          <br />
          • Compatível com Excel, Google Sheets e outros editores de planilhas
          <br />
          • Encoding UTF-8 para suporte a caracteres especiais
          <br />• Nome do arquivo inclui data e filtros aplicados
        </Typography>
      </Box>
    </Paper>
  );
};

export default ExportSection;
